import { useEffect, useMemo, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Server, Plus, Pencil, Trash2, AlertCircle, Wifi, WifiOff, Moon, RotateCw, Save,
} from "lucide-react";
import { toast } from "sonner";

const POLL_INTERVAL_SEC = 15;  // matches BIOMETRIC_POLL_INTERVAL default

/**
 * Phase 5E — Biometric Device Management
 *
 * Status freshness windows are derived from the poll interval (default 15s):
 *   green  : last successful ingest within 3 × interval  (≤ 45 s)
 *   yellow : 3× – 20× interval                            (46 s – 5 min)
 *   red    : > 20× interval                               (> 5 min)
 *
 * Tracks "last successful ingest" across BOTH paths (agent ingest + ADMS push)
 * so the page shows actual data freshness, not just heartbeat.
 */
function freshnessTone(lastSuccessIso) {
  if (!lastSuccessIso) return "red";
  const ageSec = (Date.now() - new Date(lastSuccessIso).getTime()) / 1000;
  if (ageSec < 0) return "red";
  if (ageSec <= 3 * POLL_INTERVAL_SEC) return "green";
  if (ageSec <= 20 * POLL_INTERVAL_SEC) return "yellow";
  return "red";
}

function formatAge(iso) {
  if (!iso) return "never";
  const ageSec = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
  if (ageSec < 60) return `${ageSec}s ago`;
  if (ageSec < 3600) return `${Math.floor(ageSec / 60)}m ago`;
  if (ageSec < 86400) return `${Math.floor(ageSec / 3600)}h ago`;
  return `${Math.floor(ageSec / 86400)}d ago`;
}

const TONE_CLS = {
  green: "bg-emerald-500",
  yellow: "bg-amber-500",
  red: "bg-red-500",
};

const TONE_LABEL = {
  green: "Live",
  yellow: "Stale",
  red: "Offline",
};

// ---------------------------------------------------------------------------
// Phase 5H — Night-Shift Cutoff Settings Card
// Super-admin can configure the hour-of-day below which a punch is bucketed
// to the PREVIOUS calendar day (so night-shift OUT punches at 02:30 AM land
// on the previous day's row). Also exposes a one-shot historical migration.
// ---------------------------------------------------------------------------
function NightShiftCutoffCard({ isSuperAdmin }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [draft, setDraft] = useState("5");
  const [saving, setSaving] = useState(false);
  const [migrating, setMigrating] = useState(false);
  const [lastMigration, setLastMigration] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data: d } = await api.get("/biometric/settings/night-shift-cutoff");
      setData(d);
      setDraft(String(d.hour));
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to load cutoff settings");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const dirty = data && String(draft) !== String(data.hour);

  const save = async () => {
    const h = parseInt(draft, 10);
    if (Number.isNaN(h) || h < 0 || h > 12) {
      toast.error("Cutoff hour must be between 0 and 12");
      return;
    }
    setSaving(true);
    try {
      await api.put("/biometric/settings/night-shift-cutoff", { hour: h });
      toast.success(h === 0
        ? "Night-shift bucketing DISABLED"
        : `Cutoff updated to ${h}:00 — punches before this hour will roll into previous day`);
      await load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const runMigration = async (dryRun) => {
    if (!dryRun) {
      if (!window.confirm(
        "This will RE-BUCKET every historical biometric punch whose calendar " +
        "time falls before the cutoff hour into the previous day.\n\n" +
        "It is idempotent — already-migrated rows are skipped.\n\n" +
        "Continue?",
      )) return;
    }
    setMigrating(true);
    try {
      const { data: r } = await api.post("/biometric/migrate-night-shift", { dry_run: dryRun });
      setLastMigration({ ...r, dry_run: dryRun, at: new Date().toISOString() });
      if (dryRun) {
        toast.success(`Dry-run: ${r.punches_rebucketed} punch(es) would be re-bucketed`);
      } else {
        toast.success(`Migration done — ${r.punches_rebucketed} re-bucketed, ${r.live_rows_rebuilt} live rows rebuilt`);
      }
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Migration failed");
    } finally {
      setMigrating(false);
    }
  };

  if (loading) {
    return (
      <div className="bg-white border border-slate-200 rounded-lg p-4 animate-pulse">
        <div className="h-4 w-48 bg-slate-100 rounded mb-2"></div>
        <div className="h-3 w-72 bg-slate-100 rounded"></div>
      </div>
    );
  }
  if (!data) return null;

  const sourceLabel = {
    database: "Set by admin",
    env: "From environment",
    default: `Default (${data.default}:00)`,
  }[data.source] || data.source;

  return (
    <div className="bg-gradient-to-br from-indigo-50 to-white border border-indigo-200 rounded-lg overflow-hidden"
            data-testid="night-shift-cutoff-card">
      <div className="px-4 py-3 border-b border-indigo-100 flex items-start gap-3">
        <div className="h-10 w-10 rounded-md bg-indigo-100 flex items-center justify-center flex-shrink-0">
          <Moon className="h-5 w-5 text-indigo-700" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-[10px] uppercase tracking-widest text-indigo-500 font-semibold">
            Phase 5H · Night-Shift Logic
          </div>
          <div className="text-base font-bold text-slate-900">
            Cross-Midnight Punch Cutoff
          </div>
          <div className="text-xs text-slate-600 mt-0.5 max-w-2xl">
            Any biometric punch with hour strictly less than this cutoff is
            attributed to the <strong>previous</strong> calendar day. This
            ensures a night-shift OUT-punch at e.g. 02:30 AM appears as that
            shift's last-out, not the next day's first-in.
          </div>
        </div>
      </div>

      <div className="p-4 grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="md:col-span-1">
          <Label className="text-[11px] font-semibold uppercase tracking-wide text-slate-600">
            Cutoff hour (0–12)
          </Label>
          <div className="mt-1 flex items-center gap-2">
            <Select value={String(draft)} onValueChange={setDraft} disabled={!isSuperAdmin || saving}>
              <SelectTrigger className="w-32 font-mono" data-testid="cutoff-hour-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="0" data-testid="cutoff-opt-0">0 — disabled</SelectItem>
                {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12].map((h) => (
                  <SelectItem key={h} value={String(h)} data-testid={`cutoff-opt-${h}`}>
                    {String(h).padStart(2, "0")}:00
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {isSuperAdmin && (
              <Button
                size="sm"
                disabled={!dirty || saving}
                onClick={save}
                className="brand-bg brand-bg-hover text-white disabled:opacity-50"
                data-testid="cutoff-save-btn"
              >
                <Save className="h-3.5 w-3.5 mr-1" />
                {saving ? "Saving…" : "Save"}
              </Button>
            )}
          </div>
          <div className="mt-1.5 flex items-center gap-1.5 text-[10px]">
            <span className="px-1.5 py-0.5 rounded bg-slate-100 text-slate-600 font-medium" data-testid="cutoff-source-badge">
              {sourceLabel}
            </span>
            {data.updated_at && (
              <span className="text-slate-400">
                Last changed by {data.updated_by || "—"}
              </span>
            )}
          </div>
        </div>

        <div className="md:col-span-2 bg-white border border-slate-200 rounded-md p-3">
          <div className="text-[11px] font-semibold uppercase tracking-wide text-slate-600 mb-2">
            Historical Migration
          </div>
          <p className="text-[11px] text-slate-600 leading-snug">
            Existing punches captured before this feature shipped need a one-shot
            rebucket. Safe to re-run — already-migrated rows are skipped.
          </p>
          <div className="mt-2 flex flex-wrap items-center gap-2">
            <Button
              size="sm" variant="outline"
              disabled={migrating}
              onClick={() => runMigration(true)}
              data-testid="migrate-dry-run-btn"
            >
              {migrating ? <RotateCw className="h-3.5 w-3.5 mr-1 animate-spin" /> : <AlertCircle className="h-3.5 w-3.5 mr-1" />}
              Dry-run
            </Button>
            {isSuperAdmin && (
              <Button
                size="sm"
                disabled={migrating}
                onClick={() => runMigration(false)}
                className="bg-indigo-600 hover:bg-indigo-700 text-white"
                data-testid="migrate-run-btn"
              >
                {migrating ? <RotateCw className="h-3.5 w-3.5 mr-1 animate-spin" /> : <Moon className="h-3.5 w-3.5 mr-1" />}
                Run migration
              </Button>
            )}
          </div>
          {lastMigration && (
            <div className="mt-2 text-[11px] bg-emerald-50 border border-emerald-200 rounded px-2 py-1.5 text-emerald-900"
                  data-testid="migration-result">
              <span className="font-semibold">
                {lastMigration.dry_run ? "Dry-run" : "Migration"} @ cutoff {lastMigration.cutoff_hour}:00:
              </span>{" "}
              examined {lastMigration.punches_examined},
              re-bucketed <strong>{lastMigration.punches_rebucketed}</strong>
              {!lastMigration.dry_run && (
                <>, rebuilt <strong>{lastMigration.live_rows_rebuilt}</strong> live rows</>
              )}.
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default function BiometricDevices() {
  const { user } = useAuth();
  // Rev. 2026-02 — Include hr_admin & hr_maker (maker-checker roles).
  const isAdmin = ["super_admin", "hr", "hr_admin", "hr_maker"].includes(user?.role);
  const [devices, setDevices] = useState([]);
  const [units, setUnits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [confirmDel, setConfirmDel] = useState(null);
  // tick for freshness re-render every 5 s — no fetch
  const [, setTick] = useState(0);

  useEffect(() => {
    const id = setInterval(() => setTick((t) => t + 1), 5000);
    return () => clearInterval(id);
  }, []);

  const refresh = async () => {
    setLoading(true);
    try {
      const [{ data: devs }, unitsResp] = await Promise.all([
        api.get("/biometric/devices"),
        api.get("/units").catch(() => ({ data: [] })),
      ]);
      setDevices(devs || []);
      setUnits(unitsResp.data || []);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Load failed");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { refresh(); }, []);
  // poll every 15 s to refresh status counters
  useEffect(() => {
    const id = setInterval(refresh, 15000);
    return () => clearInterval(id);
  }, []);

  const unitsById = useMemo(
    () => Object.fromEntries(units.map((u) => [u.id, u.name])),
    [units],
  );

  const onSave = async (payload, editId) => {
    try {
      if (editId) {
        await api.patch(`/biometric/devices/${editId}`, payload);
        toast.success("Device updated");
      } else {
        await api.post("/biometric/devices", payload);
        toast.success("Device registered");
      }
      setDialogOpen(false);
      setEditing(null);
      refresh();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Save failed");
    }
  };

  const onDelete = async () => {
    if (!confirmDel) return;
    try {
      await api.delete(`/biometric/devices/${confirmDel.id}`);
      toast.success(`Deleted ${confirmDel.serial}`);
      setConfirmDel(null);
      refresh();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Delete failed");
    }
  };

  return (
    <div data-testid="biometric-devices-page" className="space-y-5">
      <div className="flex items-start gap-3 flex-wrap">
        <Server className="h-7 w-7 text-red-600 mt-1" />
        <div className="flex-1 min-w-0">
          <div className="text-[10px] uppercase tracking-widest text-slate-400">Phase 5E · Admin</div>
          <h1 className="text-2xl font-bold text-slate-900" style={{ fontFamily: "Chivo" }}>
            Biometric Devices
          </h1>
          <p className="text-xs text-slate-500 mt-0.5">
            Register Mantra BIOFACE devices per unit. Status reflects the last
            successful ingest from either the on-prem agent or ADMS push.
          </p>
        </div>
        {isAdmin && (
          <Button
            className="brand-bg brand-bg-hover text-white"
            onClick={() => { setEditing(null); setDialogOpen(true); }}
            data-testid="add-device-btn"
          >
            <Plus className="h-4 w-4 mr-1.5" /> Add Device
          </Button>
        )}
      </div>

      {/* Phase 5H — Night-shift cutoff settings + historical migration */}
      {isAdmin && (
        <NightShiftCutoffCard isSuperAdmin={user?.role === "super_admin"} />
      )}

      {/* Empty state */}
      {!loading && devices.length === 0 && (
        <div className="bg-white border border-dashed border-slate-300 rounded-lg p-10 text-center" data-testid="empty-state">
          <WifiOff className="h-10 w-10 text-slate-300 mx-auto" />
          <h3 className="mt-3 text-sm font-semibold text-slate-700">No devices yet</h3>
          <p className="text-xs text-slate-500 mt-1 max-w-md mx-auto">
            {isAdmin
              ? "Click \"Add Device\" to register a Mantra BIOFACE unit. You'll need the serial number and the LAN IP from your IT team."
              : "No biometric device is registered for your unit yet. Contact HR to add one."}
          </p>
        </div>
      )}

      {/* Device cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {devices.map((d) => {
          const tone = freshnessTone(d.last_successful_ingest);
          const dotCls = TONE_CLS[tone];
          const toneLabel = TONE_LABEL[tone];
          return (
            <div
              key={d.id}
              data-testid={`device-row-${d.serial}`}
              className="bg-white border border-slate-200 rounded-lg p-4 hover:shadow-sm transition-shadow"
            >
              <div className="flex items-start justify-between gap-2">
                <div className="flex items-start gap-3 flex-1 min-w-0">
                  <span
                    className={`mt-1.5 h-2.5 w-2.5 rounded-full ${dotCls} flex-shrink-0`}
                    data-testid={`device-status-${d.serial}`}
                    title={`${toneLabel} · last ingest: ${formatAge(d.last_successful_ingest)}`}
                  />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <h3 className="font-semibold text-slate-900 truncate">{d.name}</h3>
                      <span className={`text-[10px] font-medium px-1.5 py-0.5 rounded ${
                        tone === "green" ? "bg-emerald-100 text-emerald-800" :
                          tone === "yellow" ? "bg-amber-100 text-amber-800" :
                            "bg-red-100 text-red-800"
                      }`}>
                        {toneLabel}
                      </span>
                      {!d.enabled && (
                        <span className="text-[10px] bg-slate-200 text-slate-700 px-1.5 py-0.5 rounded">Disabled</span>
                      )}
                    </div>
                    <div className="text-[11px] font-mono text-slate-500 mt-0.5">{d.serial}</div>
                  </div>
                </div>
                {isAdmin && (
                  <div className="flex gap-1 flex-shrink-0">
                    <Button
                      variant="ghost" size="sm"
                      onClick={() => { setEditing(d); setDialogOpen(true); }}
                      data-testid={`device-edit-${d.serial}`}
                    >
                      <Pencil className="h-3.5 w-3.5" />
                    </Button>
                    <Button
                      variant="ghost" size="sm"
                      className="text-red-600 hover:text-red-700"
                      onClick={() => setConfirmDel(d)}
                      data-testid={`device-delete-${d.serial}`}
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </Button>
                  </div>
                )}
              </div>

              <div className="grid grid-cols-2 gap-x-4 gap-y-2 mt-3 text-xs">
                <Detail label="Unit" value={unitsById[d.unit_id] || "—"} />
                <Detail label="Mode" value={d.mode === "agent-pull" ? (
                  <span className="text-red-700 font-medium">Agent Pull</span>
                ) : d.mode === "ADMS-push" ? (
                  <span className="text-violet-700 font-medium">ADMS Push</span>
                ) : (
                  <span className="text-slate-400">Awaiting first ingest</span>
                )} />
                <Detail label="LAN IP" value={d.ip ? <span className="font-mono">{d.ip}:{d.port}</span> : "—"} />
                <Detail label="Today's punches" value={
                  <span data-testid={`device-punchcount-${d.serial}`} className="font-mono font-semibold text-slate-900">
                    {d.today_punch_count ?? 0}
                  </span>
                } />
                <Detail label="Last successful" value={
                  <span className={tone === "green" ? "text-emerald-700" : tone === "yellow" ? "text-amber-700" : "text-red-700"}>
                    {formatAge(d.last_successful_ingest)}
                  </span>
                } />
                <Detail label="Agent ver." value={d.last_agent_version || "—"} />
              </div>

              {d.last_error && (
                <div className="mt-3 bg-red-50 border border-red-200 rounded p-2 text-[11px] text-red-800 flex items-start gap-1.5"
                     data-testid={`device-lasterror-${d.serial}`}>
                  <AlertCircle className="h-3 w-3 mt-0.5 flex-shrink-0" />
                  <div className="min-w-0">
                    <div className="font-semibold">Last error · {formatAge(d.last_error_at)}</div>
                    <div className="truncate">{d.last_error}</div>
                  </div>
                </div>
              )}

              {d.mode === "ADMS-push" && isAdmin && (
                <div className="mt-3 pt-3 border-t border-slate-100">
                  <Button
                    variant="outline" size="sm"
                    className="text-[11px] w-full"
                    onClick={async () => {
                      try {
                        const { data } = await api.get(`/biometric/devices/${d.id}`);
                        toast.success(`Last seen ${formatAge(data.last_seen)} — device reachable`);
                      } catch (e) {
                        toast.error("Connection test failed — check device + network");
                      }
                    }}
                    data-testid={`device-test-${d.serial}`}
                  >
                    <Wifi className="h-3 w-3 mr-1.5" /> Test Connection
                  </Button>
                </div>
              )}
            </div>
          );
        })}
      </div>

      <DeviceDialog
        open={dialogOpen}
        onClose={() => { setDialogOpen(false); setEditing(null); }}
        units={units}
        editing={editing}
        onSave={onSave}
      />
      <ConfirmDeleteDialog
        target={confirmDel}
        onClose={() => setConfirmDel(null)}
        onConfirm={onDelete}
      />
    </div>
  );
}

function Detail({ label, value }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-wider text-slate-400">{label}</div>
      <div className="text-slate-700">{value}</div>
    </div>
  );
}

function DeviceDialog({ open, onClose, units, editing, onSave }) {
  const [form, setForm] = useState({
    serial: "", name: "", unit_id: "", ip: "", port: 4370, enabled: true,
  });

  useEffect(() => {
    if (editing) {
      setForm({
        serial: editing.serial || "",
        name: editing.name || "",
        unit_id: editing.unit_id || "",
        ip: editing.ip || "",
        port: editing.port || 4370,
        enabled: editing.enabled !== false,
      });
    } else {
      setForm({ serial: "", name: "", unit_id: "", ip: "", port: 4370, enabled: true });
    }
  }, [editing, open]);

  const submit = () => {
    if (!form.serial.trim() || !form.name.trim() || !form.unit_id) {
      toast.error("Serial, name, and unit are required");
      return;
    }
    const payload = editing
      ? { name: form.name, unit_id: form.unit_id, ip: form.ip || null, port: Number(form.port) || 4370, enabled: form.enabled }
      : { ...form, port: Number(form.port) || 4370 };
    onSave(payload, editing?.id);
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-lg" data-testid="device-dialog">
        <DialogHeader>
          <DialogTitle>{editing ? "Edit Device" : "Register New Device"}</DialogTitle>
          <DialogDescription>
            {editing
              ? "Update device details. Serial cannot be changed once set."
              : "Add a Mantra BIOFACE device to a specific unit. Your IT team should provide the serial number and LAN IP."}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label className="text-xs">Serial Number *</Label>
            <Input
              value={form.serial}
              onChange={(e) => setForm({ ...form, serial: e.target.value })}
              disabled={!!editing}
              placeholder="e.g. BIOFACE-U1-2A8F-001"
              data-testid="device-serial-input"
            />
          </div>
          <div>
            <Label className="text-xs">Display Name *</Label>
            <Input
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              placeholder="e.g. Unit 1 Main Gate"
              data-testid="device-name-input"
            />
          </div>
          <div>
            <Label className="text-xs">Unit *</Label>
            <Select value={form.unit_id} onValueChange={(v) => setForm({ ...form, unit_id: v })}>
              <SelectTrigger data-testid="device-unit-select"><SelectValue placeholder="Select unit" /></SelectTrigger>
              <SelectContent>
                {units.map((u) => (
                  <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">LAN IP (optional)</Label>
              <Input
                value={form.ip}
                onChange={(e) => setForm({ ...form, ip: e.target.value })}
                placeholder="192.168.1.50"
                data-testid="device-ip-input"
              />
            </div>
            <div>
              <Label className="text-xs">Port</Label>
              <Input
                type="number"
                value={form.port}
                onChange={(e) => setForm({ ...form, port: e.target.value })}
                placeholder="4370"
                data-testid="device-port-input"
              />
            </div>
          </div>
          <div className="flex items-center justify-between pt-2">
            <Label className="text-xs">Enabled</Label>
            <Switch checked={form.enabled} onCheckedChange={(v) => setForm({ ...form, enabled: v })} data-testid="device-enabled-switch" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button onClick={submit} className="brand-bg brand-bg-hover text-white" data-testid="device-save-btn">
            {editing ? "Save" : "Register"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ConfirmDeleteDialog({ target, onClose, onConfirm }) {
  return (
    <Dialog open={!!target} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-md" data-testid="confirm-delete-dialog">
        <DialogHeader>
          <DialogTitle>Delete Device?</DialogTitle>
          <DialogDescription>
            This unregisters <b>{target?.serial}</b> ({target?.name}). Existing punch records are kept.
            The on-prem agent will start receiving 404 errors until re-registered.
          </DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button variant="destructive" onClick={onConfirm} data-testid="confirm-delete-btn">
            Delete
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
